import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import ForgotPasswordModal from "../../PopUpModal/ForgotPasswordModal";
import {
    updatePassword,
    EmailAuthProvider,
    reauthenticateWithCredential,
} from "firebase/auth";
import { useAuth } from "../../../context/AuthContext";
import PopUpModal from "../../PopUpModal/PopUpModal";
import ModalComponent from "../../MPS/EventRegistration/EventsModal";
import ModalLoadingState from "../../PopUpModal/ModalLoadingState";
import { CheckCircleIcon as CheckCircleIconSolid } from "@heroicons/react/solid";
import alert from "../../../assets/images/alert-circle.svg";
import { deleteEOBusinessProfile } from "../../../firebaseFunctions/deleteEOBusinessProfile";
import { subscriptionService } from "../../../services/SubscriptionService";
import { Eye, EyeOff, Lock, CreditCard, Trash2, AlertTriangle, PauseCircle, ChevronDown, CheckCircle } from "lucide-react";

const SectionCard = ({ children, className = "" }) => (
    <div className={`bg-white border border-gray-200 rounded-xl p-6 ${className}`}>
        {children}
    </div>
);

const SectionTitle = ({ icon: Icon, title, subtitle }) => (
    <div className="flex items-start gap-3 mb-5">
        <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mt-0.5">
            <Icon className="w-4 h-4 text-gray-600" />
        </div>
        <div>
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
    </div>
);

const PasswordInput = ({ label, value, onChange, placeholder, disabled }) => {
    const [show, setShow] = useState(false);
    return (
        <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">{label}</label>
            <div className="relative">
                <input
                    type={show ? "text" : "password"}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={`w-full px-3 py-2.5 pr-10 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400
                        focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition
                        ${disabled ? "opacity-50 cursor-not-allowed bg-gray-50" : ""}`}
                />
                {!disabled && (
                    <button
                        type="button"
                        onClick={() => setShow(s => !s)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                )}
            </div>
        </div>
    );
};

const AccountSettings = ({ userSignInMethod }) => {
    const { t } = useTranslation();
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);
    const [email, setEmail] = useState("");
    const { user, subscriptionTier, signOut } = useAuth();
    const userID = user.uid;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [popUpModalType, setPopUpModalType] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [pwLoading, setPwLoading] = useState(false);
    const [pauseOpen, setPauseOpen] = useState(false);
    const [pauseDuration, setPauseDuration] = useState("1");
    const [pauseLoading, setPauseLoading] = useState(false);
    const [pauseSuccess, setPauseSuccess] = useState(null);

    const isGoogleSignIn = userSignInMethod === "Google";

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");
        if (newPassword !== confirmNewPassword) {
            setError(t('settings.passwordMismatch'));
            return;
        }
        setPwLoading(true);
        try {
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, newPassword);
            setSuccess(t('settings.passwordChangeSuccess'));
            setCurrentPassword("");
            setNewPassword("");
            setConfirmNewPassword("");
        } catch {
            setError(t('settings.passwordChangeFailed'));
        } finally {
            setPwLoading(false);
        }
    };

    const handleDeleteClick = () => {
        setIsModalOpen(true);
        setPopUpModalType("delete account");
    };

    const handleDeleteAccount = async () => {
        setIsLoading(true);
        try {
            const result = await deleteEOBusinessProfile(userID);
            setPopUpModalType(result ? "delete success" : "delete failure");
        } catch (error) {
            console.error("Error deleting account:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePauseSubscriptionClick = async () => {
        setPauseLoading(true);
        try {
            const result = await subscriptionService.pauseSubscription(user.uid, pauseDuration);
            const resumeDate = new Date(result.pausedUntil).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
            setPauseSuccess(resumeDate);
            setPauseOpen(false);
        } catch (err) {
            console.error('Pause failed:', err);
            setError(t('settings.passwordChangeFailed')); // reuse generic error state
        } finally {
            setPauseLoading(false);
        }
    };

    const handleCancelSubscriptionClick = () => {
        const subject = encodeURIComponent("Request to cancel Pebble subscription");
        const body = encodeURIComponent(
            `Hi,\n\nI would like to request cancellation of my Pebble subscription.\n\nAccount Details:\n- Email: ${user.email}\n- User ID: ${user.uid}\n- Current Plan: ${subscriptionTier === 'pro' ? 'Pro ($249/month)' : 'Basic (Free)'}\n\nThank you.`
        );
        window.open(`mailto:aaronjordan6@gmail.com?subject=${subject}&body=${body}`, '_blank');
    };

    const renderModal = () => {
        switch (popUpModalType) {
            case "delete account":
                return (
                    <ModalComponent
                        title={t('settings.deleteAccountTitle')}
                        message={t('settings.deleteAccountMessage')}
                        checkboxLabel={t('settings.deleteAccountCheckbox')}
                        secondButtonLabel={t('settings.noGoBack')}
                        secondButtonVariant="transparent"
                        firstButtonLabel={t('settings.deleteAccount')}
                        firstButtonVariant="error"
                        onFirstButtonClick={handleDeleteAccount}
                        onSecondButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); }}
                    />
                );
            case "delete success":
                return (
                    <ModalComponent
                        icon={<CheckCircleIconSolid className="size-20 text-Success/700" />}
                        title={t('settings.accountDeleted')}
                        message={t('settings.returningToLogin')}
                        firstButtonLabel={t('common.continue')}
                        onFirstButtonClick={signOut}
                    />
                );
            case "delete failure":
                return (
                    <ModalComponent
                        icon={<img src={alert} alt="alert" className="size-20" />}
                        title={t('settings.errorDeletingTitle')}
                        message={t('settings.errorDeletingMessage')}
                        secondButtonLabel={t('settings.noGoBack')}
                        secondButtonVariant="transparent"
                        firstButtonLabel={t('settings.deleteAccount')}
                        firstButtonVariant="error"
                        onFirstButtonClick={handleDeleteAccount}
                        onSecondButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); }}
                    />
                );
            default:
                return <ModalLoadingState />;
        }
    };

    return (
        <>
            <PopUpModal open={isModalOpen} handleClose={() => setIsModalOpen(false)} popUpModalType={popUpModalType}>
                {!isLoading ? renderModal() : <ModalLoadingState />}
            </PopUpModal>
            <ForgotPasswordModal
                isOpen={isForgotPasswordModalOpen}
                onClose={() => { setEmail(""); setIsForgotPasswordModalOpen(false); }}
                email={email}
            />

            <div className="space-y-4 max-w-2xl">
                {/* Change Password */}
                <SectionCard>
                    <SectionTitle
                        icon={Lock}
                        title={t('settings.changePassword')}
                        subtitle={isGoogleSignIn ? t('settings.googlePasswordNote') : "Update your account password"}
                    />

                    {isGoogleSignIn ? (
                        <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                            {t('settings.googlePasswordNote')}
                        </div>
                    ) : (
                        <form onSubmit={handleChangePassword} className="space-y-3">
                            <PasswordInput
                                label={t('settings.currentPassword')}
                                value={currentPassword}
                                onChange={e => setCurrentPassword(e.target.value)}
                                placeholder={t('settings.enterCurrentPassword')}
                                disabled={isGoogleSignIn}
                            />
                            <PasswordInput
                                label={t('settings.newPassword')}
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                placeholder={t('settings.enterNewPassword')}
                                disabled={isGoogleSignIn}
                            />
                            <PasswordInput
                                label={t('settings.confirmNewPassword')}
                                value={confirmNewPassword}
                                onChange={e => setConfirmNewPassword(e.target.value)}
                                placeholder={t('settings.confirmPasswordPlaceholder')}
                                disabled={isGoogleSignIn}
                            />

                            {error && (
                                <p className="text-xs text-red-600 flex items-center gap-1.5">
                                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                                    {error}
                                </p>
                            )}
                            {success && (
                                <p className="text-xs text-green-600 flex items-center gap-1.5">
                                    <CheckCircleIconSolid className="w-3.5 h-3.5 flex-shrink-0" />
                                    {success}
                                </p>
                            )}

                            <div className="flex items-center gap-3 pt-1">
                                <button
                                    type="submit"
                                    disabled={pwLoading || isGoogleSignIn}
                                    className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
                                >
                                    {pwLoading ? "Updating…" : t('settings.changePasswordButton')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => !isGoogleSignIn && setIsForgotPasswordModalOpen(true)}
                                    disabled={isGoogleSignIn}
                                    className="text-sm font-medium text-primary-600 hover:text-primary-700 underline underline-offset-2 disabled:opacity-40 disabled:cursor-not-allowed transition"
                                >
                                    {t('settings.forgotPassword')}
                                </button>
                            </div>
                        </form>
                    )}
                </SectionCard>

                {/* Subscription */}
                <SectionCard>
                    <SectionTitle
                        icon={CreditCard}
                        title={t('settings.subscription')}
                        subtitle="Manage your Pebble subscription plan"
                    />
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div>
                            <p className="text-xs text-gray-500 mb-0.5">{t('settings.currentPlan')}</p>
                            <p className="text-sm font-semibold text-gray-900 capitalize">
                                {subscriptionTier === 'pro' ? 'Pro — $249/month' : 'Basic — Free (15% platform fee)'}
                            </p>
                        </div>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                            Active
                        </span>
                    </div>
                    <div className="mt-3">
                        <button
                            onClick={handleCancelSubscriptionClick}
                            className="text-sm text-gray-500 hover:text-gray-700 underline underline-offset-2 transition"
                        >
                            {t('settings.cancelSubscription')}
                        </button>
                    </div>
                </SectionCard>

                {/* Pause Subscription */}
                {subscriptionTier === 'pro' && (
                    <SectionCard>
                        <SectionTitle
                            icon={PauseCircle}
                            title="Pause Subscription"
                            subtitle="Take a break between events — up to 2 months/year"
                        />
                        <div className="flex items-start gap-3 px-4 py-3 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-700 mb-4">
                            <PauseCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <p>While paused, you keep full access to your CRM, analytics, and vendor history. You just can't publish new events.</p>
                        </div>
                        {pauseSuccess ? (
                            <div className="flex items-center gap-2 text-sm text-green-700">
                                <CheckCircle className="w-4 h-4" />
                                Subscription paused — billing resumes {pauseSuccess}.
                            </div>
                        ) : (
                            <>
                                <button
                                    onClick={() => setPauseOpen(o => !o)}
                                    className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition"
                                >
                                    <span>Pause my subscription</span>
                                    <ChevronDown className={`w-4 h-4 transition-transform ${pauseOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {pauseOpen && (
                                    <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1.5">How long would you like to pause?</label>
                                            <div className="flex gap-2">
                                                {["1", "2"].map(n => (
                                                    <button
                                                        key={n}
                                                        onClick={() => setPauseDuration(n)}
                                                        className={`flex-1 py-2 text-sm font-medium rounded-lg border transition ${
                                                            pauseDuration === n
                                                                ? 'border-gray-900 bg-gray-900 text-white'
                                                                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                                        }`}
                                                    >
                                                        {n} month{n === "1" ? "" : "s"}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-500">Billing resumes automatically after {pauseDuration} month{pauseDuration === "1" ? "" : "s"}. You can un-pause anytime.</p>
                                        <button
                                            onClick={handlePauseSubscriptionClick}
                                            disabled={pauseLoading}
                                            className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
                                        >
                                            {pauseLoading ? (
                                                <><svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Pausing…</>
                                            ) : 'Pause Subscription'}
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </SectionCard>
                )}

                {/* Danger Zone */}
                <SectionCard className="border-gray-200">
                    <div className="flex items-start gap-3 mb-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mt-0.5">
                            <Trash2 className="w-4 h-4 text-gray-400" />
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-gray-600">{t('settings.deleteAccount')}</h3>
                            <p className="text-xs text-gray-400 mt-0.5">Permanently delete your account and all data.</p>
                        </div>
                    </div>
                    <button
                        onClick={handleDeleteClick}
                        className="px-3 py-1.5 text-xs font-medium text-gray-500 border border-gray-300 rounded-lg hover:text-red-600 hover:border-red-300 hover:bg-red-50 transition-colors"
                    >
                        {t('settings.deleteAccount')}
                    </button>
                </SectionCard>
            </div>
        </>
    );
};

export default AccountSettings;
